import { OUTREACH_PROGRAMMES_QUERY } from "@lib/graphql/outreach";
import { useOutreachListChannel } from "@lib/hooks/useOutreachListChannel";
import { type Connection, usePaginatedList } from "@lib/hooks/usePaginatedList";
import { Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { NewProgrammeForm } from "./NewProgrammeForm";
import { ProgrammeQueriesPanel } from "./ProgrammeQueriesPanel";
import type { OutreachProgramme } from "./types";

interface Props {
  selectedProgrammeId: string | null;
  onSelectProgramme: (id: string | null) => void;
}

export function ProgrammesTab({ selectedProgrammeId, onSelectProgramme }: Props) {
  const {
    nodes: programmes,
    fetching,
    hasNextPage,
    loadMore,
    refresh,
  } = usePaginatedList<{ outreachProgrammes: Connection<OutreachProgramme> }, OutreachProgramme>({
    query: OUTREACH_PROGRAMMES_QUERY,
    getConnection: (data) => data.outreachProgrammes,
  });
  useOutreachListChannel(refresh);

  const [showForm, setShowForm] = useState(false);
  const selected = useMemo(
    () => programmes.find((p) => p.id === selectedProgrammeId) ?? null,
    [programmes, selectedProgrammeId],
  );

  if (fetching && programmes.length === 0) {
    return <p className="text-sm text-text-muted">Loading programmes…</p>;
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[18rem_1fr]">
      <aside className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-text-primary">Programmes</h3>
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-1 rounded-md bg-accent px-2 py-1 text-xs font-medium text-white hover:bg-accent-hover"
          >
            <Plus size={12} /> New
          </button>
        </div>

        {showForm && (
          <NewProgrammeForm
            onCreated={() => {
              setShowForm(false);
              refresh();
            }}
            onCancel={() => setShowForm(false)}
          />
        )}

        <ul className="space-y-1">
          {programmes.map((p) => (
            <li key={p.id}>
              <button
                onClick={() => onSelectProgramme(p.id)}
                className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm ${
                  p.id === selectedProgrammeId
                    ? "bg-accent/10 text-text-primary"
                    : "text-text-secondary hover:bg-card-hover"
                }`}
              >
                <span className="truncate">{p.name}</span>
                {p.pendingDraftCount > 0 && (
                  <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800 dark:bg-amber-400/20 dark:text-amber-100">
                    {p.pendingDraftCount}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>

        {hasNextPage && (
          <button
            onClick={loadMore}
            disabled={fetching}
            className="w-full rounded-md border border-input-border bg-card px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-card-hover disabled:opacity-50"
          >
            {fetching ? "Loading..." : "Load more"}
          </button>
        )}
      </aside>

      <section className="space-y-4">
        {selected ? (
          <>
            <header>
              <h2 className="text-lg font-semibold text-text-primary">{selected.name}</h2>
              <p className="mt-1 text-sm text-text-muted">{selected.description}</p>
              {selected.toneGuidance && (
                <details className="mt-2 text-sm text-text-secondary">
                  <summary className="cursor-pointer">Tone guidance</summary>
                  <pre className="mt-2 whitespace-pre-wrap rounded bg-card-alt p-3 text-xs">
                    {selected.toneGuidance}
                  </pre>
                </details>
              )}
            </header>
            <ProgrammeQueriesPanel
              programmeId={selected.id}
              queries={selected.programmeQueries ?? []}
              onChanged={refresh}
            />
          </>
        ) : (
          <div className="rounded-lg border-2 border-dashed border-border-dashed p-8 text-center">
            <p className="text-sm text-text-muted">
              Select a programme to view or edit its queries.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
